import React, { useCallback, useEffect, useState } from "react";
import {
  Modal,
  Stack,
  Group,
  Text,
  Select,
  Card,
  Badge,
  Button,
  Center,
  Loader,
  NumberInput,
  Avatar,
  Divider,
  Progress,
  UnstyledButton,
  SegmentedControl,
  ActionIcon,
  Switch,
  Box,
  Menu,
  TextInput,
} from "@mantine/core";

import {
  IconUser,
  IconMail,
  IconPhone,
  IconChevronDown,
  IconCircle,
  IconCircleCheck,
  IconLock,
  IconTrash,
  IconChevronLeft,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import { handleApiError, handleGenericError } from "../../utils/errorHandler";
import DealReview from "../Organization/DealReview";
import CreateCustomDealModal from "../Organization/CreateCustomDealModal";

// Normalize a DateInput value (string or Date) to a YYYY-MM-DD string for the
// API, or null when empty.
const toISODateString = date => {
  if (!date) return null;
  if (typeof date === "string") {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  }
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Group the flat company_default_scales array by scale_type into the shape the
// embedded deal editor expects ({ COMMISSION, OVERRIDE, MD }).
const groupScalesByType = scales => {
  const out = { COMMISSION: [], OVERRIDE: [], MD: [] };
  (Array.isArray(scales) ? scales : []).forEach(scale => {
    const type = scale.scale_type;
    if (!out[type]) out[type] = [];
    out[type].push({
      ...scale,
      scale_lines: Array.isArray(scale.default_scale_lines)
        ? scale.default_scale_lines
        : scale.scale_lines || [],
    });
  });
  return out;
};

const docTextId = obj =>
  obj?.document_text_id ?? obj?.document_text?.id ?? null;

const toScaleLines = lines =>
  (Array.isArray(lines) ? lines : []).map(l => ({
    revenue_amount: Number(l.revenue_amount) || 0,
    percentage: Number(l.percentage) || 0,
  }));

const templateBonus = b => ({
  name: b.name,
  bonus_type_category: b.bonus_type_category,
  bonus_type_earned_by_item: b.bonus_type_earned_by_item,
  bonus_type_earned_by_type: b.bonus_type_earned_by_type,
  item_name: b.item_name ?? null,
  item_monetary_value: b.item_monetary_value ?? null,
  bonus_amount: b.bonus_amount ?? null,
  bonus_revenue_amount: b.bonus_revenue_amount ?? null,
  bonus_accounts_amount: b.bonus_accounts_amount ?? null,
  bonus_recruits_amount: b.bonus_recruits_amount ?? null,
  recurring_initial_amount: b.recurring_initial_amount ?? null,
  recurring_repeated_amount: b.recurring_repeated_amount ?? null,
  recurring_start_date: b.recurring_start_date ?? null,
  recurring_end_date: b.recurring_end_date ?? null,
  recurring_schedule_type: b.recurring_schedule_type ?? null,
  recurring_day_of_week: b.recurring_day_of_week ?? null,
  recurring_day_of_month: b.recurring_day_of_month ?? null,
});

const templateExpense = e => ({
  name: e.name,
  expense_type_category: e.expense_type_category,
  backend_percentage: e.backend_percentage ?? null,
  backend_fixed_amount: e.backend_fixed_amount ?? null,
  recurring_frontend_amount: e.recurring_frontend_amount ?? null,
  recurring_schedule_type: e.recurring_schedule_type ?? null,
  recurring_day_of_week: e.recurring_day_of_week ?? null,
  recurring_day_of_month: e.recurring_day_of_month ?? null,
});

// Build the deal DTO (the shape /v1/ps/review and the deal endpoints expect)
// straight from a company template, for the non-customized path.
const templateToDealPayload = t => {
  if (!t) return null;
  const users = (Array.isArray(t.deal_users) ? t.deal_users : []).map(u => {
    let upfront_pay;
    if (u.upfront_pay) {
      upfront_pay = { ...u.upfront_pay };
      delete upfront_pay.document_text_id;
    }
    return {
      name: u.name,
      percentage: u.deal_percentage,
      commission_scale_lines: toScaleLines(u.commission_scale?.scale_lines),
      upfront_pay,
      bonuses: (u.default_bonuses || []).map(templateBonus),
    };
  });
  const payload = {
    selling_year_id: t.selling_year_id,
    name: t.name,
    deal_type: t.deal_type,
    is_customizable: t.is_customizable,
    template_id: t.template_id,
    users,
    expenses: (t.default_expenses || []).map(templateExpense),
  };
  if (t.deal_type === "REP") {
    payload.allow_override = !!t.has_override;
    const overrideDocId = docTextId(t.override_scale);
    if (t.has_override && overrideDocId != null) {
      payload.override_document_text_id = overrideDocId;
    }
    payload.override_scale_lines = t.has_override
      ? toScaleLines(t.override_scale?.scale_lines)
      : undefined;
  } else if (t.deal_type === "MD") {
    const mdDocId = docTextId(t.md_scale);
    if (mdDocId != null) payload.md_document_text_id = mdDocId;
    payload.md_scale_lines = toScaleLines(t.md_scale?.scale_lines);
  }
  return payload;
};

const ORANGE = "var(--mantine-color-orange-6)";

function ScaleTable({ title, col2Label, lines, onChange, scaleOptions }) {
  const updateLine = (index, field, value) =>
    onChange(lines.map((l, i) => (i === index ? { ...l, [field]: value } : l)));

  const addLine = () =>
    onChange([...lines, { revenue_amount: 0, percentage: 0 }]);

  const removeLine = index => onChange(lines.filter((_, i) => i !== index));

  const hasOptions = Array.isArray(scaleOptions) && scaleOptions.length > 0;

  return (
    <Card withBorder radius="md" p={0} style={{ overflow: "hidden" }}>
      {hasOptions ? (
        <Menu withinPortal position="bottom" width="target">
          <Menu.Target>
            <UnstyledButton style={{ width: "100%" }}>
              <Group justify="center" gap={4} py="xs">
                <Text fw={700} size="sm">
                  {title}
                </Text>
                <IconChevronDown size={14} />
              </Group>
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown>
            {scaleOptions.map((opt, i) => (
              <Menu.Item
                key={i}
                onClick={() => onChange(toScaleLines(opt.scale_lines))}
              >
                {opt.name}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      ) : (
        <Text ta="center" fw={700} py="xs" size="sm">
          {title}
        </Text>
      )}
      <Divider />
      {/* Orange header */}
      <Group gap={0} style={{ background: ORANGE }}>
        <Text
          style={{ flex: 1 }}
          ta="center"
          fw={700}
          size="sm"
          py="xs"
          c="white"
        >
          Sales
        </Text>
        <Divider orientation="vertical" color="rgba(255,255,255,0.4)" />
        <Text
          style={{ flex: 1 }}
          ta="center"
          fw={700}
          size="sm"
          py="xs"
          c="white"
        >
          {col2Label}
        </Text>
        <Box w={40} />
      </Group>
      <Divider />
      {/* Start row — locked, only percentage editable */}
      <Group gap={0} style={{ background: "var(--mantine-color-gray-1)" }}>
        <Text style={{ flex: 1 }} ta="center" fw={700} size="sm" py={6}>
          Start
        </Text>
        <Divider orientation="vertical" />
        <Box style={{ flex: 1 }} py={4} px="xs">
          <NumberInput
            value={lines[0]?.percentage ?? 0}
            onChange={v => updateLine(0, "percentage", Number(v))}
            variant="filled"
            size="xs"
            styles={{ input: { textAlign: "center" } }}
            hideControls
          />
        </Box>
        <Box
          w={40}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconLock size={14} color="var(--mantine-color-gray-5)" />
        </Box>
      </Group>
      {/* Editable rows */}
      {lines.slice(1).map((line, i) => (
        <React.Fragment key={i}>
          <Divider />
          <Group gap={0}>
            <Box style={{ flex: 1 }} py={4} px="xs">
              <NumberInput
                value={line.revenue_amount}
                onChange={v => updateLine(i + 1, "revenue_amount", Number(v))}
                variant="filled"
                size="xs"
                styles={{ input: { textAlign: "center" } }}
                hideControls
                thousandSeparator=","
              />
            </Box>
            <Divider orientation="vertical" />
            <Box style={{ flex: 1 }} py={4} px="xs">
              <NumberInput
                value={line.percentage}
                onChange={v => updateLine(i + 1, "percentage", Number(v))}
                variant="filled"
                size="xs"
                styles={{ input: { textAlign: "center" } }}
                hideControls
              />
            </Box>
            <Box
              w={40}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ActionIcon
                variant="subtle"
                color="red"
                size="sm"
                onClick={() => removeLine(i + 1)}
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Box>
          </Group>
        </React.Fragment>
      ))}
      <Divider />
      <UnstyledButton
        onClick={addLine}
        style={{ display: "block", width: "100%" }}
      >
        <Text ta="center" py="xs" c="orange" fw={600} size="sm">
          + Add row
        </Text>
      </UnstyledButton>
    </Card>
  );
}

// Pick a selling year, then a company deal template from that year to start a
// new deal from.
function AddDealModal({
  opened,
  onClose,
  sellingYears,
  initialYearId,
  onCreated,
}) {
  const { getVerifiedJWT, user } = useAuth();
  const { showSuccess } = useToast();
  const [sending, setSending] = useState(false);

  const [step, setStep] = useState("select");
  const [selectedYearId, setSelectedYearId] = useState(null);
  const [overview, setOverview] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const templates = Array.isArray(overview.company_templates)
    ? overview.company_templates.filter(
        t => String(t.selling_year_id) === String(selectedYearId)
      )
    : [];

  // Step 2 - deal details.
  const [guarantor, setGuarantor] = useState(null);
  // Per deal user (keyed by index in reviewData.users): seller name/email,
  // selling start/end dates, and projected revenue estimate.
  const [userDetails, setUserDetails] = useState({});

  // Guarantor options come from the overview API response. Company deals are
  // labeled by company name; other deals are labeled by their deal users (shown
  // with avatar + name in the dropdown via renderGuarantorOption).
  const guarantorList = Array.isArray(overview.guarantor_options)
    ? overview.guarantor_options
    : [];

  const guarantorById = guarantorList.reduce((acc, g) => {
    acc[String(g.id)] = g;
    return acc;
  }, {});

  const guarantorUserName = u =>
    u.name ||
    `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
    u.email ||
    "";

  const guarantorLabel = g => {
    if (g.is_company_deal) return g.company_name || `Guarantor ${g.id}`;
    const names = (Array.isArray(g.deal_users) ? g.deal_users : [])
      .map(guarantorUserName)
      .filter(Boolean);
    return names.join(", ") || g.company_name || `Guarantor ${g.id}`;
  };

  const guarantorOptions = guarantorList.map(g => ({
    value: String(g.id),
    label: guarantorLabel(g),
  }));

  const renderGuarantorOption = ({ option }) => {
    const g = guarantorById[option.value];
    if (!g) return option.label;
    if (g.is_company_deal) {
      return (
        <Text size="sm" fw={600}>
          {g.company_name || option.label}
        </Text>
      );
    }
    const users = Array.isArray(g.deal_users) ? g.deal_users : [];
    return (
      <Group gap="lg" wrap="wrap" style={{ flex: 1 }}>
        {users.map(u => (
          <Group key={u.id} gap="xs" wrap="nowrap">
            <Avatar src={u.profile_image_url} radius="xl" size="sm">
              {(u.first_name || "?").charAt(0)}
            </Avatar>
            <Text size="sm">{guarantorUserName(u)}</Text>
          </Group>
        ))}
      </Group>
    );
  };

  // Embedded editor (only for customizable templates): holds the edited deal
  // payload once the user finishes the wizard. The wizard renders inline as the
  // "customize" step rather than as a second modal.
  const [editedDeal, setEditedDeal] = useState(null);

  // Step 2 — editable pay details.
  const [localDealType, setLocalDealType] = useState("REP");
  const [localMdLines, setLocalMdLines] = useState([
    { revenue_amount: 0, percentage: 0 },
  ]);
  const [localCommissionLines, setLocalCommissionLines] = useState([
    { revenue_amount: 0, percentage: 0 },
  ]);
  const [splitMD, setSplitMD] = useState(false);
  const [allowOverride, setAllowOverride] = useState(false);
  const [localOverrideLines, setLocalOverrideLines] = useState([
    { revenue_amount: 0, percentage: 0 },
  ]);

  const [sellerName, setSellerName] = useState("");
  const [sellerEmail, setSellerEmail] = useState("");
  const [sellerPhone, setSellerPhone] = useState("");

  useEffect(() => {
    if (opened) {
      setStep("select");
      setSelectedYearId(initialYearId != null ? String(initialYearId) : null);
      setSelectedTemplateId(null);
      setGuarantor(null);
      setUserDetails({});
      setEditedDeal(null);
      setSellerName(
        [user?.firstName, user?.lastName].filter(Boolean).join(" ") || ""
      );
      setSellerEmail(user?.email || "");
      setSellerPhone(user?.phone || "");
    }
  }, [opened, initialYearId, user]);

  const fetchTemplates = useCallback(async () => {
    if (!opened || !selectedYearId) {
      setOverview({});
      return;
    }
    try {
      setLoading(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(
        getApiEndpoint(`/v1/ps/overview?selling_year_id=${selectedYearId}`),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        const data = (result && result.data) || result || {};
        setOverview(data);
      } else {
        setOverview({});
      }
    } catch (err) {
      setOverview({});
    } finally {
      setLoading(false);
    }
  }, [opened, selectedYearId, getVerifiedJWT]);

  useEffect(() => {
    fetchTemplates();
    setSelectedTemplateId(null);
  }, [fetchTemplates]);

  const selected = templates.find(
    t => String(t.id) === String(selectedTemplateId)
  );

  // The deal DTO posted to /v1/ps/review. When the deal was customized through
  // the embedded editor, review the edited payload (already DTO-shaped — just add
  // the template fields); otherwise build the DTO from the template as-is.
  const reviewData = editedDeal
    ? {
        ...editedDeal,
        name: editedDeal.name ?? selected?.name ?? "Deal",
        is_customizable: selected?.is_customizable,
        template_id: selected?.template_id ?? null,
      }
    : templateToDealPayload(selected);

  const dealUsers = Array.isArray(reviewData?.users) ? reviewData.users : [];

  // Create the user deal: POST the deal DTO plus the guarantor and the per-user
  // seller / dates / projected-revenue details to /v1/ps/user-deal.
  const handleSend = async () => {
    if (!reviewData) return;
    try {
      setSending(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const payload = {
        ...reviewData,
        selling_year_id: selectedYearId,
        template_id: selected?.template_id ?? selected?.id ?? null,
        // The company deal template this deal was built from.
        default_deal_id: selected?.id != null ? Number(selected.id) : null,
        guarantor_id: guarantor != null ? Number(guarantor) : null,
        users: dealUsers.map((u, index) => {
          const d = userDetails[index] || {};
          return {
            ...u,
            direct_seller_name: sellerName || d.name || "",
            direct_seller_email: sellerEmail || d.email || "",
            direct_seller_phone: sellerPhone || d.phone || "",
            selling_start_date: toISODateString(d.startDate),
            selling_end_date: toISODateString(d.endDate),
            projected_revenue_estimate:
              d.revenue === "" || d.revenue == null ? null : Number(d.revenue),
          };
        }),
      };

      const response = await fetch(getApiEndpoint("/v1/ps/user-deal"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        showSuccess("Document sent successfully!");
        onClose();
        if (onCreated) onCreated();
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message || "Failed to send document"
        );
      }
    } catch (err) {
      handleGenericError(err, "Failed to send document");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title={
          step === "paydetails" ? (
            <Group gap="xs">
              <ActionIcon variant="subtle" onClick={() => setStep("select")}>
                <IconChevronLeft size={18} />
              </ActionIcon>
              <Text fw={600}>Pay Details</Text>
            </Group>
          ) : step === "review" ? (
            <Group gap="xs">
              <ActionIcon
                variant="subtle"
                onClick={() => setStep("paydetails")}
              >
                <IconChevronLeft size={18} />
              </ActionIcon>
              <Text fw={600}>Review Offer</Text>
            </Group>
          ) : (
            "Add Deal"
          )
        }
        size="lg"
      >
        {step === "select" ? (
          <Stack gap="lg">
            {/* Progress */}
            <Stack gap={4}>
              <Progress value={33} size="xs" color="orange" />
              <Text size="xs" c="dimmed" ta="center" tt="uppercase" fw={600}>
                Step 1 / 3
              </Text>
            </Stack>

            {/* Direct Seller */}
            <Stack gap="xs">
              <Text fw={700} size="sm">
                Direct Seller
              </Text>
              <Card withBorder radius="md" p={0}>
                <Group px="md" py="xs" wrap="nowrap" gap="sm">
                  <IconUser size={18} color="var(--mantine-color-orange-6)" />
                  <TextInput
                    placeholder="Full name"
                    value={sellerName}
                    onChange={e => setSellerName(e.currentTarget.value)}
                    variant="unstyled"
                    size="sm"
                    style={{ flex: 1 }}
                    styles={{ input: { fontWeight: 600 } }}
                  />
                </Group>
                <Divider />
                <Group px="md" py="xs" wrap="nowrap" gap="sm">
                  <IconMail size={18} color="var(--mantine-color-orange-6)" />
                  <TextInput
                    placeholder="Email address"
                    value={sellerEmail}
                    onChange={e => setSellerEmail(e.currentTarget.value)}
                    variant="unstyled"
                    size="sm"
                    style={{ flex: 1 }}
                    styles={{ input: { fontWeight: 600 } }}
                  />
                </Group>
                <Divider />
                <Group px="md" py="xs" wrap="nowrap" gap="sm">
                  <IconPhone size={18} color="var(--mantine-color-orange-6)" />
                  <TextInput
                    placeholder="Phone number"
                    value={sellerPhone}
                    onChange={e => setSellerPhone(e.currentTarget.value)}
                    variant="unstyled"
                    size="sm"
                    style={{ flex: 1 }}
                    styles={{ input: { fontWeight: 600 } }}
                  />
                </Group>
              </Card>
            </Stack>

            {/* Split MD Deal toggle */}
            <Group justify="space-between">
              <Text size="sm" fw={700}>
                Split MD Deal
              </Text>
              <Switch
                checked={splitMD}
                onChange={e => {
                  const next = e.currentTarget.checked;
                  setSplitMD(next);
                  if (next) {
                    const isCompatible = t =>
                      t.deal_type === "MD" && (t.is_split || t.is_customizable);
                    if (selected && !isCompatible(selected)) {
                      setSelectedTemplateId(null);
                    }
                  }
                }}
                color="orange"
              />
            </Group>

            {/* Guarantor */}
            <Stack gap="xs">
              <Text fw={700} size="sm">
                Guarantor
              </Text>
              <Select
                placeholder="Select guarantor"
                data={guarantorOptions}
                renderOption={renderGuarantorOption}
                value={guarantor}
                onChange={setGuarantor}
                searchable
                clearable
              />
            </Stack>

            {/* Offer Template */}
            <Stack gap="xs">
              <Group justify="space-between" align="center">
                <Text fw={700} size="sm">
                  Offer Template
                </Text>
                <Select
                  placeholder="Year"
                  data={(Array.isArray(sellingYears) ? sellingYears : []).map(
                    y => ({
                      value: String(y.id),
                      label: y.name,
                    })
                  )}
                  value={selectedYearId}
                  onChange={value => {
                    setSelectedYearId(value);
                    setSelectedTemplateId(null);
                  }}
                  allowDeselect={false}
                  w={100}
                  size="xs"
                  rightSection={<IconChevronDown size={12} />}
                />
              </Group>

              {loading ? (
                <Center py="md">
                  <Loader size="sm" />
                </Center>
              ) : templates.length === 0 ? (
                <Text size="sm" c="dimmed">
                  No templates for this selling year.
                </Text>
              ) : (
                <Card withBorder radius="md" p={0}>
                  {templates.map((template, i) => {
                    const isSelected =
                      String(template.id) === String(selectedTemplateId);
                    const dealTypeLabel =
                      template.deal_type === "MD" ? "Marketing Deal" : "Rep";
                    const isDisabled =
                      splitMD &&
                      !(
                        template.deal_type === "MD" &&
                        (template.is_split || template.is_customizable)
                      );
                    return (
                      <React.Fragment key={template.id}>
                        {i > 0 && <Divider />}
                        <UnstyledButton
                          onClick={() =>
                            !isDisabled && setSelectedTemplateId(template.id)
                          }
                          style={{
                            display: "block",
                            width: "100%",
                            opacity: isDisabled ? 0.4 : 1,
                            cursor: isDisabled ? "not-allowed" : "pointer",
                          }}
                        >
                          <Group
                            px="md"
                            py="sm"
                            wrap="nowrap"
                            justify="space-between"
                          >
                            <Stack gap={2}>
                              <Group gap="xs" wrap="nowrap">
                                <Text size="sm" fw={600}>
                                  {template.name}
                                </Text>
                                {template.is_customizable && (
                                  <Badge
                                    size="xs"
                                    variant="filled"
                                    color="orange"
                                    tt="uppercase"
                                  >
                                    Customizable
                                  </Badge>
                                )}
                              </Group>
                              <Text size="xs" c="dimmed">
                                {dealTypeLabel}
                              </Text>
                            </Stack>
                            {isSelected ? (
                              <IconCircleCheck
                                size={22}
                                color="var(--mantine-color-orange-6)"
                              />
                            ) : (
                              <IconCircle
                                size={22}
                                color="var(--mantine-color-gray-4)"
                              />
                            )}
                          </Group>
                        </UnstyledButton>
                      </React.Fragment>
                    );
                  })}
                </Card>
              )}
            </Stack>

            <Button
              fullWidth
              color="orange"
              disabled={!selected}
              rightSection={<span>›</span>}
              onClick={() => {
                setEditedDeal(null);
                const rd = templateToDealPayload(selected);
                const dealType = selected?.deal_type ?? "REP";
                setLocalDealType(dealType);
                const mdLines =
                  Array.isArray(rd?.md_scale_lines) &&
                  rd.md_scale_lines.length > 0
                    ? rd.md_scale_lines
                    : [{ revenue_amount: 0, percentage: 0 }];
                setLocalMdLines(mdLines);
                const commLines =
                  Array.isArray(rd?.users?.[0]?.commission_scale_lines) &&
                  rd.users[0].commission_scale_lines.length > 0
                    ? rd.users[0].commission_scale_lines
                    : [{ revenue_amount: 0, percentage: 0 }];
                setLocalCommissionLines(commLines);
                if (dealType !== "MD") setSplitMD(false);
                setAllowOverride(!!selected?.has_override);
                const overrideLines =
                  Array.isArray(rd?.override_scale_lines) &&
                  rd.override_scale_lines.length > 0
                    ? rd.override_scale_lines
                    : [{ revenue_amount: 0, percentage: 0 }];
                setLocalOverrideLines(overrideLines);
                setStep("paydetails");
              }}
            >
              Continue
            </Button>
          </Stack>
        ) : step === "customize" ? (
          <CreateCustomDealModal
            embedded
            opened={opened}
            agreements={
              Array.isArray(overview.uploaded_document_templates)
                ? overview.uploaded_document_templates
                : []
            }
            sellingYearId={selectedYearId}
            scales={groupScalesByType(overview.company_default_scales)}
            upfrontPay={overview.upfront_pay}
            companyUpfrontTypes={overview.ps_company_upfront_types || []}
            defaultBonuses={overview.default_bonuses}
            defaultExpenses={overview.default_expenses}
            deal={selected}
            hideTemplateStep
            onBack={() => setStep("select")}
            onComplete={payload => {
              setEditedDeal(payload);
              setStep("paydetails");
            }}
          />
        ) : step === "paydetails" ? (
          <Stack gap="lg">
            {/* Progress */}
            <Stack gap={4}>
              <Progress value={66} size="xs" color="orange" />
              <Text size="xs" c="dimmed" ta="center" tt="uppercase" fw={600}>
                Step 2 / 3
              </Text>
            </Stack>

            {/* Deal type toggle */}
            <Stack gap={4}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Deal Type
              </Text>
              <SegmentedControl
                fullWidth
                value={localDealType}
                onChange={setLocalDealType}
                data={[
                  { value: "REP", label: "Rep" },
                  { value: "MD", label: "Marketing Deal" },
                ]}
                styles={{
                  root: {
                    background: "var(--mantine-color-gray-1)",
                    "--sc-label-color": "white",
                  },
                  indicator: { background: "#1a1a1a" },
                  label: { color: "var(--mantine-color-dark-9)" },
                }}
              />
            </Stack>

            {/* MD Scale — shown when MD type */}
            {localDealType === "MD" && (
              <ScaleTable
                title="MD Scale"
                col2Label="MD %"
                lines={localMdLines}
                onChange={setLocalMdLines}
                scaleOptions={
                  groupScalesByType(overview.company_default_scales).MD
                }
              />
            )}

            {/* Split MD toggle */}
            {localDealType === "MD" && (
              <Group justify="space-between">
                <Text size="sm" fw={600}>
                  Split MD
                </Text>
                <Switch
                  checked={splitMD}
                  onChange={e => setSplitMD(e.currentTarget.checked)}
                  color="orange"
                />
              </Group>
            )}

            {/* Commission Scale */}
            <ScaleTable
              title="Commission Scale"
              col2Label="Commission"
              lines={localCommissionLines}
              onChange={setLocalCommissionLines}
              scaleOptions={
                groupScalesByType(overview.company_default_scales).COMMISSION
              }
            />

            {/* Rep override options */}
            {localDealType === "REP" && (
              <Group justify="space-between">
                <Text size="sm" fw={600}>
                  Allow Override
                </Text>
                <Switch
                  checked={allowOverride}
                  onChange={e => setAllowOverride(e.currentTarget.checked)}
                  color="orange"
                />
              </Group>
            )}
            {localDealType === "REP" && allowOverride && (
              <ScaleTable
                title="Override Scale"
                col2Label="Override %"
                lines={localOverrideLines}
                onChange={setLocalOverrideLines}
                scaleOptions={
                  groupScalesByType(overview.company_default_scales).OVERRIDE
                }
              />
            )}

            <Button
              fullWidth
              color="orange"
              rightSection={<span>›</span>}
              onClick={() => setStep("review")}
            >
              Review offer
            </Button>
          </Stack>
        ) : (
          <Stack gap="md">
            {/* Progress */}
            <Stack gap={4}>
              <Progress value={100} size="xs" color="orange" />
              <Text size="xs" c="dimmed" ta="center" tt="uppercase" fw={600}>
                Step 3 / 3
              </Text>
            </Stack>

            {reviewData && <DealReview {...reviewData} />}

            <Group justify="space-between" mt="md">
              <Button
                variant="subtle"
                onClick={() => setStep("paydetails")}
                disabled={sending}
              >
                Back
              </Button>
              <Button
                onClick={handleSend}
                loading={sending}
                disabled={!reviewData}
              >
                Send Document
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}

export default AddDealModal;
